import { environment } from "@app/relay/relay-environment";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { factSheetSettingsPage_MeQuery } from "@app/queries/factSheetSettingsPage_MeQuery.graphql";
import type { factSheetSettingsPage_ConfigSetMutation } from "@app/queries/factSheetSettingsPage_ConfigSetMutation.graphql";
import PublicFactSheetPage, { formatMetric } from "./public-fact-sheet-page";

const meQuery = graphql`
  query factSheetSettingsPage_MeQuery {
    me {
      publicSlug
      factSheet {
        key
        label
        value
        unit
        benchmark
        benchmarkLabel
        visible
      }
    }
  }
`;

const configSetMutation = graphql`
  mutation factSheetSettingsPage_ConfigSetMutation($visibility: [FactSheetVisibilityInputObject!]!) {
    factSheetConfigSet(visibility: $visibility) {
      user {
        id
      }
      errors {
        message
      }
    }
  }
`;

type Metrics = NonNullable<factSheetSettingsPage_MeQuery["response"]["me"]>["factSheet"];

export default function FactSheetSettingsPage() {
  const [metrics, setMetrics] = useState<Metrics>([]);
  const [slug, setSlug] = useState<string | null>(null);
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    fetchQuery<factSheetSettingsPage_MeQuery>(environment, meQuery, {})
      .toPromise()
      .then((d) => {
        const fs = (d?.me?.factSheet ?? []) as Metrics;
        setMetrics(fs);
        setSlug(d?.me?.publicSlug ?? null);
        setVisible(Object.fromEntries(fs.map((m) => [m.key, m.visible])));
      });
  }, []);

  useEffect(reload, [reload]);

  const save = () => {
    setSaving(true);
    commitMutation<factSheetSettingsPage_ConfigSetMutation>(environment, {
      mutation: configSetMutation,
      variables: { visibility: metrics.map((m) => ({ key: m.key, visible: visible[m.key] ?? true })) },
      onCompleted: () => {
        setSaving(false);
        reload();
      },
      onError: () => setSaving(false),
    });
  };

  const previewMetrics = metrics.filter((m) => visible[m.key]);

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Public fact sheet</h1>
          <p className="mt-1 text-sm text-gray-500">
            Choose which metrics to show on your public page
            {slug ? (
              <>
                {" "}at{" "}
                <a href={`/${slug}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                  /{slug}
                </a>
              </>
            ) : (
              " (an admin sets your URL slug)"
            )}
            .
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPreview((p) => !p)} className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700">
            {preview ? "Hide preview" : "Preview my fact sheet"}
          </button>
          <button onClick={save} disabled={saving} className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="max-w-2xl divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
        {metrics.map((m) => (
          <label key={m.key} className="flex cursor-pointer items-center justify-between px-4 py-3">
            <div>
              <span className="text-sm font-medium text-gray-800">{m.label}</span>
              <span className="ml-3 text-sm text-gray-400">{formatMetric(m.value, m.unit)}</span>
            </div>
            <input
              type="checkbox"
              checked={visible[m.key] ?? true}
              onChange={(e) => setVisible((v) => ({ ...v, [m.key]: e.target.checked }))}
              className="h-4 w-4"
            />
          </label>
        ))}
      </div>

      {preview && (
        <div className={clsx("mt-8 overflow-hidden rounded-2xl ring-1 ring-gray-200")}>
          <PublicFactSheetPage previewMetrics={previewMetrics} />
        </div>
      )}
    </div>
  );
}
